import { useState, useCallback, useEffect, useMemo, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import DropdownConfigIndex from "./DropdownConfigIndex";
import {
  fetchSettingsDropdownValues,
  fetchBasicStandards,
  saveTwoColumnListEntry,
} from "../../API_Service/Settings_Slice";
import { useGlobalLoader } from "../../Common/GlobalLoader";
import {
  Button,
  Icon,
  Spinner,
  FormField,
  TextInput,
  InlineAlert,
  DrawerShell,
  Chip,
  employeeID,
  companyID,
  sanitizeLabName,
  normalizeSpaces,
} from "../../Common/UIComponents";

const VALUE_MAX_LENGTH = 50;

const normalizeTerm = (term = "") =>
  term.toString().trim().replace(/\s+/g, "").toUpperCase();

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const normalizeItem = (item = {}) => ({
  ...item,
  id:    item.id ?? item.ID ?? item.code ?? item.CODE ??
         String(item.value ?? item.Value ?? item.label ?? item.Label ?? ""),
  label: String(item.label ?? item.Label ?? item.Value ?? item.value ?? "").trim(),
  value: item.value ?? item.Value ?? item.label ?? item.Label,
});

const IS_ACTIVITY = (chipLabel = "") =>
  chipLabel.trim().toLowerCase() === "activity";

const INITIAL_FORM = {
  termId:       0,
  termCode:     "",
  termLabel:    "",
  value:        "",
  activityType: "",
  valueId:      0,
};

function useDropdownForm({ onSubmit, onSuccess, onCancel, initialData, isEditMode, isActivityChip }) {
  const { showLoader, hideLoader } = useGlobalLoader();

  const [form,        setForm]        = useState(INITIAL_FORM);
  const [errors,      setErrors]      = useState({});
  const [status,      setStatus]      = useState("idle");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialData && !initialized && initialData.termId) {
      setForm({
        termId:       initialData.termId       ?? 0,
        termCode:     initialData.termCode     ?? "",
        termLabel:    initialData.termLabel    ?? "",
        value:        initialData.value        ?? "",
        activityType: initialData.activityType ?? "",
        valueId:      initialData.valueId      ?? 0,
      });
      setInitialized(true);
    }
  }, [initialData, initialized]);

  const validate = useCallback(() => {
    const next = {};
    if (!form.termId && !form.termCode.trim()) next.term  = "Please choose a dropdown type.";
    if (!form.value.trim())                    next.value = isActivityChip ? "Activity is required." : "Value is required.";
    if (isActivityChip && !form.activityType.trim()) next.activityType = "Type is required for Activity.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form, isActivityChip]);

  const handleChange = useCallback((field) => (e) => {
    let val = typeof e.target.value === "string" ? e.target.value.replace(/^\s+/, "") : e.target.value;
    if (field === "value" && typeof val === "string") val = sanitizeLabName(val);
    setForm((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (field === "value") setStatus("idle");
  }, []);

  const handleSelectType = useCallback((type) => {
    setForm((prev) => {
      if (String(type.id ?? "") === String(prev.termId ?? "") && prev.termId) return prev;
      return {
        termId:       type.id    ?? type.ID    ?? 0,
        termCode:     String(type.CODE  ?? type.code  ?? type.value ?? "").trim(),
        termLabel:    String(type.Value ?? type.value ?? type.label ?? "").trim(),
        value:        "",
        activityType: "",
        valueId:      0,
      };
    });
    setErrors({});
    setStatus("idle");
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    showLoader();
    try {
      const payload = {
        Term:      normalizeTerm(form.termLabel),
        Value:     normalizeSpaces(form.value),
        Value2:    isActivityChip ? normalizeSpaces(form.activityType) : undefined,
        ID:        isEditMode ? (form.valueId ?? 0) : 0,
        CompanyID: companyID,
        CreatedBy: employeeID,
      };
      const result = await onSubmit(payload);
      if (result?.success) {
        setStatus("success");
        if (!isEditMode) {
          setForm((prev) => ({
            ...INITIAL_FORM,
            termId:    prev.termId,
            termCode:  prev.termCode,
            termLabel: prev.termLabel,
          }));
          setInitialized(false);
        }
        onSuccess?.(payload);
      } else if (result?.duplicate) {
        setStatus("duplicate");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      hideLoader();
    }
  }, [form, isEditMode, isActivityChip, validate, onSubmit, onSuccess, showLoader, hideLoader]);

  const handleCancel = useCallback(() => {
    setForm(INITIAL_FORM);
    setInitialized(false);
    setErrors({});
    setStatus("idle");
    onCancel?.();
  }, [onCancel]);

  return {
    form, errors, status,
    handleChange, handleSelectType,
    handleSubmit, handleCancel,
  };
}

const DropdownTypeSelector = memo(({ types = [], selectedId, onSelect, error, isEditMode = false }) => {
  const visibleTypes = isEditMode
    ? types.filter((item) => String(item.id ?? "") === String(selectedId))
    : types;

  return (
    <FormField id="dropdown-type" label="Dropdown Type" required error={error}>
      {visibleTypes.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {visibleTypes.map((item) => {
            const label    = item.label ?? item.Value ?? item.value ?? "Unknown";
            const isActive = !!selectedId && String(item.id ?? "") === String(selectedId);
            return (
              <Chip key={`type-${item.id}`} active={isActive} onClick={() => onSelect(item)}>
                {label}
              </Chip>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-[#6e797a]">Loading dropdown types…</p>
      )}
    </FormField>
  );
});
DropdownTypeSelector.displayName = "DropdownTypeSelector";

const STATUS_ALERTS = {
  success:   { type: "success", message: "Dropdown saved successfully."                                       },
  error:     { type: "error",   message: "Something went wrong. Please try again."                            },
  duplicate: { type: "warning", message: "This value already exists for the selected dropdown type."          },
};

const DropdownDrawerForm = memo(({
  isOpen, onClose, onSuccess,
  dropdownTypes = [],
  onTypeSelected, onSubmit,
  isEditMode = false, initialData = null,
  isActivityChip = false,
}) => {
  const {
    form, errors, status,
    handleChange, handleSelectType,
    handleSubmit, handleCancel,
  } = useDropdownForm({ onSubmit, onSuccess, onCancel: onClose, initialData, isEditMode, isActivityChip });

  const isLoading = status === "loading";
  const alert     = status === "success"
    ? { type: "success", message: isEditMode ? "Dropdown updated successfully." : "Dropdown saved successfully." }
    : STATUS_ALERTS[status] ?? null;

  const footer = (
    <>
      <Button variant="ghost" onClick={handleCancel} disabled={isLoading}>Cancel</Button>
      <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
        {isLoading
          ? <><Spinner /> Saving…</>
          : <><Icon name="save" className="text-[18px]" filled />{isEditMode ? "Update" : "Save"} Dropdown</>
        }
      </Button>
    </>
  );

  return (
    <DrawerShell isOpen={isOpen} onClose={onClose}
      title={isEditMode ? "Edit Dropdown" : "Add Dropdown"}
      subtitle="Configure a dynamic value for the system."
      icon={isActivityChip ? "bolt" : "list_alt"}
      footer={footer}>
      <form id="dropdown-form" onSubmit={handleSubmit} className="space-y-6" noValidate>

        <DropdownTypeSelector
          types={dropdownTypes}
          selectedId={form.termId}
          onSelect={(item) => { handleSelectType(item); onTypeSelected?.(item); }}
          error={errors.term}
          isEditMode={isEditMode}
        />

        <FormField id="dropdown-value" label={isActivityChip ? "Activity" : "Value"} required error={errors.value}>
          <TextInput
            id="dropdown-value"
            value={form.value}
            onChange={handleChange("value")}
            placeholder={isActivityChip ? "Enter activity name" : "Enter display value"}
            error={errors.value}
            maxLength={VALUE_MAX_LENGTH}
          />
          {form.value.length > 0 && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${form.value.length === VALUE_MAX_LENGTH ? "text-red-500 font-semibold" : "text-[#6e797a]"}`}>
              <span>{form.value.length}/{VALUE_MAX_LENGTH}</span>
              {form.value.length === VALUE_MAX_LENGTH && (
                <span className="text-red-500">— limit reached</span>
              )}
            </div>
          )}
        </FormField>

        {/* Type dropdown — only when Activity chip selected */}
        {isActivityChip && (
          <FormField id="activity-type" label="Type" required error={errors.activityType}>
            <div className="relative">
              <select
                id="activity-type"
                value={form.activityType}
                onChange={handleChange("activityType")}
                className={`w-full h-11 bg-[#f1f4f4] rounded-xl px-4 text-sm font-medium text-[#181c1d] border appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all ${errors.activityType ? "border-red-400 ring-2 ring-red-300" : "border-transparent"}`}
              >
                <option value="">Select type</option>
                <option value="Billable">Billable</option>
                <option value="NonBillable">NonBillable</option>
              </select>
              <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#6e797a] text-[18px]">expand_more</span>
            </div>
          </FormField>
        )}

        {alert && <InlineAlert type={alert.type} message={alert.message} />}
      </form>
    </DrawerShell>
  );
});
DropdownDrawerForm.displayName = "DropdownDrawerForm";

export default function DropdownConfigCreate({
  embedded   = false,
  onCancel,
  onSuccess,
  presetTerm = null,   // when embedded, force-select a dropdown type (e.g. "BASICSTANDARD")
} = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode   = !embedded && location.state?.mode === "edit";
  const editData     = embedded ? null : (location.state?.data ?? null);
  const isModalEntry = !embedded && !!(location.state?.backgroundLocation);
  const bgActiveChip = embedded ? null : (location.state?.activeChip ?? null);

  // Effective preset term — either from explicit prop (embedded usage)
  // or from the active chip carried in navigation state (Add Dropdown from index).
  const effectivePresetTerm = presetTerm ?? bgActiveChip?.label ?? bgActiveChip?.value ?? null;

  const [selectedTermLabel, setSelectedTermLabel] = useState(
    isEditMode ? (editData?.type ?? "") : (effectivePresetTerm ?? "")
  );
  const isActivityChip = useMemo(() => IS_ACTIVITY(selectedTermLabel), [selectedTermLabel]);

  const [drawerOpen,     setDrawerOpen]     = useState(true);
  const [selectedTerm,   setSelectedTerm]   = useState(null);
  const [dropdownValues, setDropdownValues] = useState([]);
  const [basicStandards, setBasicStandards] = useState([]);

  useEffect(() => {
    dispatch(fetchSettingsDropdownValues())
      .then((res) => setDropdownValues(toArray(res).map(normalizeItem)));
  }, [dispatch]);

  // Auto-select the dropdown type once the list arrives, when either:
  // — `presetTerm` prop is set (embedded usage), OR
  // — `bgActiveChip` is passed via navigation state (Add Dropdown clicked from index page).
  useEffect(() => {
    if (!effectivePresetTerm || selectedTerm || dropdownValues.length === 0) return;
    const wanted = normalizeTerm(effectivePresetTerm);
    const match  = dropdownValues.find((d) =>
      normalizeTerm(d.Value ?? d.value ?? d.label) === wanted ||
      normalizeTerm(d.label ?? "") === wanted
    );
    if (match) {
      setSelectedTerm(match);
      setSelectedTermLabel(match.label ?? match.value ?? effectivePresetTerm);
    }
  }, [effectivePresetTerm, dropdownValues, selectedTerm]);

  useEffect(() => {
    if (!selectedTerm || isEditMode) return;
    const term = normalizeTerm(selectedTerm.label ?? selectedTerm.value ?? "");
    dispatch(fetchBasicStandards({ term, companyID }))
      .then((res) => setBasicStandards(toArray(res).map(normalizeItem)))
      .catch(() => setBasicStandards([]));
  }, [selectedTerm, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode || !editData?.type) return;
    dispatch(fetchBasicStandards({ term: normalizeTerm(editData.type), companyID }))
      .then((res) => setBasicStandards(toArray(res).map(normalizeItem)))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initialEditData = useMemo(() => {
    // ── Edit mode: use the existing record ──
    if (isEditMode && editData) {
      const matched = dropdownValues.find(
        (d) => d.Value === editData.type || d.label === editData.type || d.value === editData.type
      );
      return {
        termLabel:    editData.type         ?? "",
        value:        editData.value        ?? "",
        activityType: editData.activityType ?? "",
        valueId:      editData.id           ?? 0,
        termId:       matched?.id           ?? 0,
        termCode:     String(matched?.code  ?? matched?.value ?? ""),
      };
    }

    // ── Add mode with a preset (from prop OR active chip in nav state):
    //    pre-select the dropdown type so the form opens with it already chosen ──
    if (!isEditMode && effectivePresetTerm && dropdownValues.length > 0) {
      const wanted = normalizeTerm(effectivePresetTerm);
      const matched = dropdownValues.find((d) =>
        normalizeTerm(d.Value ?? d.value ?? d.label) === wanted ||
        normalizeTerm(d.label ?? "") === wanted
      );
      if (matched) {
        return {
          termLabel:    String(matched.Value ?? matched.value ?? matched.label ?? "").trim(),
          value:        "",
          activityType: "",
          valueId:      0,
          termId:       matched.id ?? 0,
          termCode:     String(matched.code ?? matched.value ?? ""),
        };
      }
    }

    return null;
  }, [isEditMode, editData, dropdownValues, effectivePresetTerm]);

  const handleSave = useCallback(async (payload) => {
    const res  = await dispatch(saveTwoColumnListEntry(payload));
    const data = res?.payload?.data ?? res?.payload;
    if (data === -1) return { success: false, duplicate: true };
    if (data !== undefined && data !== null) {
      // Embedded: hand the new id + value back to the caller so it can
      // refresh its dropdown list and auto-select the new entry.
      if (embedded) onSuccess?.(Number(data), payload?.Value ?? "");
      return { success: true, data };
    }
    return { success: false };
  }, [dispatch, embedded, onSuccess]);

  const handleSuccess = useCallback(() => {
    dispatch(fetchSettingsDropdownValues())
      .then((res) => setDropdownValues(toArray(res).map(normalizeItem)));
    if (selectedTerm && !isEditMode) {
      const term = normalizeTerm(selectedTerm.label ?? selectedTerm.value ?? "");
      dispatch(fetchBasicStandards({ term, companyID }))
        .then((res) => setBasicStandards(toArray(res).map(normalizeItem)));
    }
  }, [dispatch, selectedTerm, isEditMode]);

  const handleClose = useCallback(() => {
    setDrawerOpen(false);
    if (embedded) {
      onCancel?.();
    } else {
      navigate("/application-config");
    }
  }, [navigate, embedded, onCancel]);

  return (
    <>
      {isModalEntry && <DropdownConfigIndex initialChip={bgActiveChip} />}

      <DropdownDrawerForm
        isOpen={drawerOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
        dropdownTypes={dropdownValues}
        existingValues={basicStandards}
        onTypeSelected={(term) => { setSelectedTerm(term); setSelectedTermLabel(term?.label ?? term?.value ?? ""); }}
        onSubmit={handleSave}
        initialData={initialEditData}
        isEditMode={isEditMode}
        isActivityChip={isActivityChip}
      />
    </>
  );
}
